import { useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/Pagination';
import { usePendingUsers } from '../hooks/usePendingUsers';

export const AdminPendingTab = () => {
  const {
    pendingUsers, pendingTotal, pendingPage, pendingTotalPages,
    setPendingPage, fetchPendingUsers, deletePendingUser,
  } = usePendingUsers();

  useEffect(() => {
    fetchPendingUsers(pendingPage);
  }, [pendingPage]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Pending Users ({pendingTotal})</CardTitle>
              {/* Was #000000 on the dark card — invisible (found Jun 2026). */}
              <p className="text-sm text-neutral-300">Users awaiting email verification</p>
            </div>
            <Button
              onClick={() => fetchPendingUsers(pendingPage)}
              variant="outline"
              size="sm"
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {pendingUsers.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No pending users</p>
          ) : (
            <div className="space-y-3">
              {pendingUsers.map((pendingUser) => {
                const createdAt = new Date(pendingUser.created_at);
                const expiresAt = pendingUser.verification_expires ? new Date(pendingUser.verification_expires) : null;
                const isExpired = expiresAt && expiresAt < new Date();
                
                return (
                  <Card key={pendingUser.id} className="bg-yellow-50 border-yellow-200">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{pendingUser.full_name}</h3>
                            {isExpired && (
                              <Badge variant="destructive" className="text-xs">Verification Expired</Badge>
                            )}
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p><strong>Username:</strong> {pendingUser.username}</p>
                            <p><strong>Email:</strong> {pendingUser.email}</p>
                            <p><strong>Registered:</strong> {createdAt.toLocaleString()}</p>
                            {expiresAt && (
                              <p><strong>Link expires:</strong> {expiresAt.toLocaleString()}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`Delete pending user ${pendingUser.username}? They can re-register with the same email after deletion.`)) {
                              deletePendingUser(pendingUser.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              <Pagination
                page={pendingPage}
                totalPages={pendingTotalPages}
                onChange={setPendingPage}
                testidPrefix="pending-users-pagination"
                theme="light"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPendingTab;
